import { TableScroll } from '@web/components/table-scroll.tsx';
import type { OperationalReportId, ReportCell, ReportTable } from '@shared/operational-reports.ts';
import { useReportPaging } from '@web/components/report-pagination.tsx';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { reportsApi } from '@web/api/endpoints.ts';
import { useSession } from '@web/state/session.tsx';
import { classes } from '@web/components/ui.tsx';
import { getDateLocale } from '@web/i18n/locale.ts';
import { reportRetry, ReportBoundary, SectionCard, type RangeParams } from '../shared.tsx';

export function OperationalSection({
  reportId,
  range,
  scope,
}: {
  reportId: OperationalReportId;
  range: RangeParams;
  scope?: 'all';
}) {
  const { t } = useTranslation();
  const paging = useReportPaging({ reportId, ...range, scope });
  const pages = paging.pages;
  const query = useQuery({
    queryKey: ['reports', reportId, range, scope, pages, paging.identity],
    queryFn: () => reportsApi.operational(reportId, { ...range, scope, pages }),
    retry: reportRetry,
  });
  const { locations } = useSession();
  return (
    <ReportBoundary query={query} paging={paging}>
      {(report) => (
        <div className="space-y-4">
          <SectionCard title={t(`reports.tab.${reportId}`)} csvHref={reportsApi.csvUrl(reportId, { ...range, scope })}>
            <p className="text-sm">{t(`reports.operational.notes.${reportId}`)}</p>
            <p className="field-hint mt-2">{t('reports.operational.calendar', { timezone: report.timezone })}</p>
            {report.basic ? (
              <p className="field-hint mt-2">
                {t(`reports.operational.${reportId === 'money-received' ? 'basicMoney' : 'basicCancellation'}`)}
              </p>
            ) : null}
          </SectionCard>
          {report.tables.map((table) => (
            <SectionCard key={table.key} collection={table.key} title={t(`reports.operational.tables.${table.key}`)}>
              <p className="field-hint mb-2">
                {t('reports.operational.count', { shown: table.rows.length, total: table.totalRows })}
              </p>
              {!table.rows.length ? (
                <p>{t('reports.operational.empty')}</p>
              ) : (
                <OperationalTable table={table} locations={locations} />
              )}
            </SectionCard>
          ))}
        </div>
      )}
    </ReportBoundary>
  );
}

const OPERATIONAL_MONEY_COLUMNS = new Set([
  'amount',
  'chargeTips',
  'customerPaidFees',
  'charges',
  'refunds',
  'netReceipts',
  'assessedFee',
]);
const OPERATIONAL_NUMBER_COLUMNS = new Set([
  ...OPERATIONAL_MONEY_COLUMNS,
  'scoredInspections',
  'averageScore',
  'complaints',
  'ordinaryRequests',
  'acknowledged',
  'targetsMet',
  'targetsMissed',
  'score',
  'scoredItems',
  'acknowledgmentHours',
  'targetHours',
  'items',
  'noticeHours',
  'offers',
  'filledHours',
  'hoursToFill',
  'cancellations',
  'visits',
  'lines',
]);
const OPERATIONAL_STATE_COLUMNS = new Set(['status', 'targetStatus', 'feeStatus', 'offerStatus']);
const PAYMENT_COLUMN_ORDER = [
  'date',
  'customer',
  'amount',
  'type',
  'status',
  'method',
  'chargeTips',
  'customerPaidFees',
  'currency',
  'reference',
  'locationId',
  'paymentId',
  'instant',
  'dateBasis',
  'refundOf',
];

function OperationalTable({
  table,
  locations,
}: {
  table: ReportTable;
  locations: ReadonlyArray<{ id: string; name: string }>;
}) {
  const { t } = useTranslation();
  const title = t(`reports.operational.tables.${table.key}`);
  const columns = table.columns.map((column, index) => ({ column, index }));
  // Keep the everyday payment facts first. The original column indices still
  // address every API value; export order and the underlying report are unchanged.
  if (table.key === 'paymentLedger') {
    const rank = (column: string) => {
      const index = PAYMENT_COLUMN_ORDER.indexOf(column);
      return index < 0 ? PAYMENT_COLUMN_ORDER.length : index;
    };
    columns.sort((a, b) => rank(a.column) - rank(b.column));
  }
  return (
    <>
      <TableScroll
        className="overflow-x-auto rounded-md border border-[color:var(--color-line)]"
        role="region"
        aria-label={title}
        tabIndex={0}
      >
        <table className="data-table w-full min-w-max text-sm">
          <caption className="sr-only">{title}</caption>
          <thead className="bg-[color:var(--color-surface)]">
            <tr>
              {columns.map(({ column }) => (
                <th
                  scope="col"
                  key={column}
                  className={classes(
                    'border-b border-[color:var(--color-line)] px-4 py-3 align-bottom text-xs font-semibold text-[color:var(--color-muted)]',
                    OPERATIONAL_NUMBER_COLUMNS.has(column) ? 'text-right' : 'text-left',
                  )}
                >
                  <span className="block max-w-[12rem] whitespace-normal leading-5">
                    {t(`reports.operational.columns.${column}`)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className="border-b border-[color:var(--color-line)] last:border-0 even:bg-[color:var(--color-surface)] hover:bg-[color:var(--color-brand-soft)]"
              >
                {columns.map(({ column, index }, columnIndex) => (
                  <td
                    key={column}
                    className={classes(
                      'px-4 py-3 align-middle leading-5',
                      OPERATIONAL_NUMBER_COLUMNS.has(column) ? 'tabular text-right whitespace-nowrap' : 'text-left',
                      column === 'amount' || column === 'netReceipts' || column === 'customer' || columnIndex === 0
                        ? 'font-medium'
                        : '',
                    )}
                  >
                    <OperationalCell cell={row[index] ?? null} column={column} locations={locations} />
                    {table.key === 'paymentLedger' &&
                    column === 'customer' &&
                    typeof row[table.columns.indexOf('type')] === 'string' ? (
                      <div className="mt-0.5 block text-xs font-normal text-[color:var(--color-muted)]">
                        {t(`reports.operational.values.${row[table.columns.indexOf('type')]}`, {
                          defaultValue: String(row[table.columns.indexOf('type')]),
                        })}
                      </div>
                    ) : null}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
      <p className="mt-2 text-xs text-[color:var(--color-muted)] sm:hidden">{t('reports.operational.scrollHint')}</p>
    </>
  );
}

function OperationalCell({
  cell,
  column,
  locations,
}: {
  cell: ReportCell;
  column: string;
  locations: ReadonlyArray<{ id: string; name: string }>;
}) {
  const { t } = useTranslation();
  if (cell === null)
    return (
      <span className="text-[color:var(--color-muted)]">
        {column === 'locationId'
          ? t('reports.operational.companyWide')
          : column === 'reason'
            ? t('reports.operational.values.notGiven')
            : '—'}
      </span>
    );
  if (typeof cell === 'number')
    return new Intl.NumberFormat(getDateLocale(), {
      minimumFractionDigits: OPERATIONAL_MONEY_COLUMNS.has(column) ? 2 : 0,
      maximumFractionDigits: 2,
    }).format(cell);
  if (typeof cell === 'boolean') return t(`reports.operational.${cell ? 'yes' : 'no'}`);
  if (column === 'locationId') return locations.find((location) => location.id === cell)?.name ?? cell;
  const translated =
    OPERATIONAL_STATE_COLUMNS.has(column) || ['type', 'kind', 'initiator', 'dateBasis'].includes(column);
  const label = translated ? t(`reports.operational.values.${cell}`, { defaultValue: cell }) : cell;
  if (OPERATIONAL_STATE_COLUMNS.has(column)) {
    const positive = ['succeeded', 'resolved', 'met', 'done', 'filled', 'paid', 'collected'].includes(cell);
    const caution = ['pending', 'pending_approval', 'open', 'overdue', 'missed', 'failed'].includes(cell);
    return (
      <span
        className={classes(
          'badge',
          positive
            ? 'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)] text-[color:var(--color-brand)]'
            : caution
              ? 'border-[color:var(--color-warning)] bg-[color:var(--color-warning-soft)] text-[color:var(--color-warning)]'
              : 'border-[color:var(--color-line)] bg-white text-[color:var(--color-muted)]',
        )}
      >
        {label}
      </span>
    );
  }
  if (column.endsWith('Id') || column === 'reference' || column === 'refundOf') {
    return (
      <span title={label} className="whitespace-nowrap font-mono text-xs text-[color:var(--color-muted)]">
        {label}
      </span>
    );
  }
  return (
    <span
      className={classes(
        'block',
        ['customer', 'site', 'reason', 'dateBasis', 'addOn', 'label'].includes(column)
          ? 'table-text max-w-[14rem] whitespace-normal'
          : 'whitespace-nowrap',
      )}
    >
      {label}
    </span>
  );
}
